import { AlertTriangle, X } from "lucide-react";
import { Button } from "./Button";
import { useFocusTrap } from "../../../hooks/useFocusTrap";

interface ConfirmDangerModalProps {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Texto del botón mientras `busy` (default "Borrando…") */
  busyLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}

export function ConfirmDangerModal({
  title,
  description,
  confirmLabel = "Borrar",
  cancelLabel = "Cancelar",
  busyLabel = "Borrando…",
  onConfirm,
  onCancel,
  busy = false,
}: ConfirmDangerModalProps) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-danger-title"
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 id="confirm-danger-title" className="font-semibold text-lg text-slate-900">
            {title}
          </h3>
          <button
            onClick={onCancel}
            disabled={busy}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={22} className="text-red-600" />
            </div>
            <p className="text-slate-600 text-sm leading-relaxed pt-1">
              {description}
            </p>
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={busy}
            className="flex-1"
          >
            {cancelLabel}
          </Button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 text-white ${
              busy
                ? "bg-red-400 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700 active:scale-[0.98] shadow-lg shadow-red-500/25"
            }`}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDangerModal;
